import { CHROME_EXTENSION_ID } from 'lib/useExtensionDetection';

type ChromeRuntime = {
  sendMessage: (
    extensionId: string,
    message: unknown,
    callback: (response: ExtensionMessageResponse | undefined) => void,
  ) => void;
  lastError?: { message?: string };
};

export type ExtensionMessageResponse = {
  success?: boolean;
  error?: string;
  filled?: string[];
  errors?: string[];
  locations?: string[];
  skills?: string[];
  titles?: string[];
};

export type ExtensionCallFailure = {
  ok: false;
  error: string;
  extensionNotRunning?: boolean;
};

export type ExtensionCallSuccess = {
  ok: true;
  response: ExtensionMessageResponse;
};

export type ExtensionCallResult = ExtensionCallFailure | ExtensionCallSuccess;

export type PostToBoardResult = {
  ok: boolean;
  error?: string;
  extensionNotRunning?: boolean;
  filled?: string[];
  errors?: string[];
};

export type LocationSuggestionsResult = {
  ok: boolean;
  locations?: string[];
  error?: string;
  extensionNotRunning?: boolean;
};

export type SkillSuggestionsResult = {
  ok: boolean;
  skills?: string[];
  error?: string;
  extensionNotRunning?: boolean;
};

export type TitleSuggestionsResult = {
  ok: boolean;
  titles?: string[];
  error?: string;
  extensionNotRunning?: boolean;
};

const EXTENSION_NOT_RUNNING_MESSAGE =
  'Harbor extension is not running. Install or enable the extension and try again.';

const isExtensionNotRunningError = (message?: string): boolean => {
  if (!message) {
    return false;
  }

  const normalized = message.toLowerCase();
  return (
    normalized.includes('message port closed before a response was received') ||
    normalized.includes('could not establish connection') ||
    normalized.includes('receiving end does not exist')
  );
};

const getExtensionNotRunningResult = (): ExtensionCallFailure => ({
  ok: false,
  extensionNotRunning: true,
  error: EXTENSION_NOT_RUNNING_MESSAGE,
});

const getChromeRuntime = (): ChromeRuntime | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const runtime = (window as typeof window & { chrome?: { runtime?: ChromeRuntime } }).chrome?.runtime;
  return runtime?.sendMessage ? runtime : null;
};

export function sendExtensionMessage(message: unknown): Promise<ExtensionCallResult> {
  return new Promise(resolve => {
    const runtime = getChromeRuntime();

    if (!runtime) {
      resolve({ ok: false, error: 'Chrome runtime not available' });
      return;
    }

    try {
      runtime.sendMessage(CHROME_EXTENSION_ID, message, response => {
        if (runtime.lastError) {
          const errorMessage = runtime.lastError.message || 'Extension not reachable';

          if (isExtensionNotRunningError(errorMessage)) {
            resolve(getExtensionNotRunningResult());
            return;
          }

          resolve({ ok: false, error: errorMessage });
          return;
        }

        if (!response || typeof response !== 'object') {
          resolve(getExtensionNotRunningResult());
          return;
        }

        resolve({ ok: true, response });
      });
    } catch {
      resolve(getExtensionNotRunningResult());
    }
  });
}

export async function postJobToBoard(messageType: string, fields: unknown): Promise<PostToBoardResult> {
  const result = await sendExtensionMessage({ type: messageType, fields });

  if (!result.ok) {
    return result;
  }

  return {
    ok: !!result.response.success,
    error: result.response.error,
    filled: Array.isArray(result.response.filled) ? result.response.filled : [],
    errors: Array.isArray(result.response.errors) ? result.response.errors : [],
  };
}

export async function fetchExtensionLocationSuggestions(
  messageType: string,
  payload: Record<string, unknown>,
): Promise<LocationSuggestionsResult> {
  const result = await sendExtensionMessage({ type: messageType, ...payload });

  if (!result.ok) {
    return result;
  }

  return {
    ok: !!result.response.success,
    locations: Array.isArray(result.response.locations) ? result.response.locations : [],
    error: result.response.error,
  };
}

export async function fetchExtensionSkillSuggestions(
  messageType: string,
  payload: Record<string, unknown>,
): Promise<SkillSuggestionsResult> {
  const result = await sendExtensionMessage({ type: messageType, ...payload });

  if (!result.ok) {
    return result;
  }

  return {
    ok: !!result.response.success,
    skills: Array.isArray(result.response.skills) ? result.response.skills : [],
    error: result.response.error,
  };
}

export async function fetchExtensionTitleSuggestions(
  messageType: string,
  payload: Record<string, unknown>,
): Promise<TitleSuggestionsResult> {
  const result = await sendExtensionMessage({ type: messageType, ...payload });

  if (!result.ok) {
    return result;
  }

  return {
    ok: !!result.response.success,
    titles: Array.isArray(result.response.titles) ? result.response.titles : [],
    error: result.response.error,
  };
}
